import type { ContentType } from "@brookbush/database";

import { useAuth, useSignUp } from "@clerk/nextjs";
import type { AuthenticateWithRedirectParams } from "@clerk/types";
import { getCookie } from "cookies-next";
import { useRouter } from "next/compat/router";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";

interface FullWidthCTAProps {
	becomeAMemberContent?: string;
	signUpContent?: string;
	contentId?: string;
	contentType?: ContentType;
	/** Optional override for the membership-model closing sentence. */
	footerSentence?: string;
}
const FullWidthCTAProps = ({ footerSentence }: FullWidthCTAProps) => {
	const router = useRouter();
	const path = router?.asPath.split("?")[0];

	const { signUp } = useSignUp();

	const { isSignedIn } = useAuth();

	const signUpWithSocial = (
		strategy: AuthenticateWithRedirectParams["strategy"],
	) =>
		signUp?.authenticateWithRedirect({
			strategy,
			redirectUrl: "/sign-up/sso-callback",
			unsafeMetadata: {
				signUpPath: path,
				isTrialUser: !!getCookie("is-trial-user") || !!getCookie("referrer-id"),
				ipAddress: getCookie("ip-address"),
				userAgent: getCookie("user-agent"),
				auditedCourseId: getCookie("audited-course-id"),
				auditedVideoId: getCookie("audited-video-id"),
				auditedGlossaryTermId: getCookie("audited-glossary-term-id"),
				auditedSampleProgramId: getCookie("audited-sample-program-id"),
				onboarding: {
					allCoursesTutorial: null,
					courseTutorial: null,
					certificationTutorial: null,
				},
				referrerId: getCookie("referrer-id"),
				shouldReceiveGiftCard: getCookie("utm-gift-card"),
				posthogSessionId: posthog.get_session_id(),
			},

			redirectUrlComplete: "/",
		});

	if (isSignedIn) return null;

	return (
		<div className="flex-col gap-6 bg-gradient-to-br from-sky-50 via-amber-50 to-emerald-50 p-6 sm:gap-10 sm:p-16 lg:flex-row xl:flex dark:from-sky-300/20 dark:via-amber-300/20 dark:to-emerald-300/20">
			<div className="flex flex-col items-center justify-center gap-4 sm:gap-8">
				<h3 className="max-w-4xl font-bold text-3xl sm:text-4xl lg:text-5xl">
					4 Certifications, 200+ Courses, 500+ videos, an AI tutor and so much
					more!
				</h3>

				<h4 className="max-w-4xl font-medium text-xl sm:text-2xl lg:text-3xl">
					The Brookbush Institute (BI) continues to improve affordability,
					access, flexibility, and convenience to the highest-quality education.
				</h4>

				<p className="max-w-4xl text-lg sm:text-xl">
					{footerSentence ??
						"The BI is the only approved/accredited certification and continuing education course provider with a true monthly membership model (cancel anytime)."}
				</p>

				<p className="mb-8 max-w-4xl text-lg sm:text-xl">
					This reduces the initial cost of education to just 3-5% of comparable
					education, improving access to complete, continue, or just
					&quot;try&quot; education with low financial risk. Don&apos;t get
					fooled by great marketing to make a large purchase for potentially
					sub-optimal education. Become a member, and find out why we think this
					is the way education should be!
				</p>
			</div>

			<div className="max-h-max shrink-0 flex-col gap-6 rounded-xl border bg-white p-8 shadow-black/5 shadow-xl sm:m-auto sm:flex sm:max-w-md sm:gap-7 sm:p-16 dark:bg-slate-800 dark:shadow-black/30">
				<div className="mx-auto flex flex-col gap-6 font-medium text-lg sm:w-80 sm:gap-8">
					<button
						onClick={() => signUpWithSocial("oauth_google")}
						className="flex items-center gap-3 rounded-full border px-4 py-2 transition-all hover:bg-black/5 sm:px-8 sm:py-3"
					>
						<Image
							fetchPriority="high"
							src="/icons/google-icon.png"
							alt="Google Logo"
							width={24}
							height={24}
						/>
						Continue With Google
					</button>

					<button
						onClick={() => signUpWithSocial("oauth_facebook")}
						className="flex items-center gap-3 rounded-full border px-4 py-2 transition-all hover:bg-black/5 sm:px-8 sm:py-3"
					>
						<Image
							fetchPriority="high"
							src="/icons/facebook-icon.png"
							alt="Google Logo"
							width={24}
							height={24}
						/>
						Continue With Facebook
					</button>

					<button
						onClick={() => signUpWithSocial("oauth_apple")}
						className="flex items-center gap-3 rounded-full border px-4 py-2 transition-all hover:bg-black/5 sm:px-8 sm:py-3"
					>
						<Image
							fetchPriority="high"
							src="/icons/apple-icon.png"
							alt="Google Logo"
							width={24}
							height={24}
							className="dark:invert"
						/>
						Continue With Apple
					</button>

					<div className="flex items-center justify-center gap-4">
						<hr className="flex-1" />
						<span className="font-light">or</span>
						<hr className="flex-1" />
					</div>
					<Link
						href="/sign-up"
						className="flex justify-center rounded-full bg-gradient-to-r from-sky-400 to-sky-600 px-4 py-2 text-white transition-all hover:brightness-95 sm:px-8 sm:py-3 dark:from-sky-500 dark:to-sky-700"
					>
						Sign Up with Email Address
					</Link>

					<div className="-mb-1 flex justify-center gap-1 font-normal sm:-mb-6">
						Already Have an Account?{" "}
						<Link
							href="/sign-in"
							className="group inline-flex max-w-max items-center gap-1 font-medium text-primary-600 hover:underline dark:text-primary-400"
						>
							Sign In
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FullWidthCTAProps;
